import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiSave, FiSend, FiPrinter, FiSearch, FiX, FiLock, FiPaperclip, FiUser, FiPlus, FiTrash2, FiAward, FiFileText, FiBookmark } from 'react-icons/fi';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import LetterEditor from '../components/LetterEditor';
import LetterPhotoPanel from '../components/LetterPhotoPanel';
import LetterAttachmentPanel from '../components/LetterAttachmentPanel';
import LetterTemplatePicker from '../components/LetterTemplatePicker';
import { findLetterPlaceholders } from '../utils/placeholders';

const empty = {
  recipient_name: '',
  recipient_company: '',
  recipient_address: '',
  recipient_city_state_zip: '',
  recipient_email: '',
  subject: '',
  body_html: '',
  related_type: null,
  related_id: null,
  related_label: null,
  attach_reference_pdf: false,
  fields: [],
  closing: 'Sincerely,',
  apply_seal: false,
  visibility: 'public',
  status: 'draft',
};

// Complimentary closes offered in the dropdown. A certification letter to a
// county reads better as "Respectfully," than "Sincerely,".
const CLOSINGS = ['Sincerely,', 'Respectfully,', 'Respectfully submitted,', 'Best regards,', 'Regards,'];

// The recipient currently being typed (after the last comma) — the letter Email field
// accepts several addresses separated by commas.
const lastToken = (s) => ((s || '').split(',').pop() || '').trim();
// Split a comma-separated recipient string into clean addresses.
const splitRecipients = (s) => (s || '').split(',').map(e => e.trim()).filter(Boolean);

function LetterEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.can_manage_payroll;
  // Only offer the seal toggle to someone who actually has credentials on file.
  // A seal image alone is enough; the numbers are printed if present.
  const canSeal = !!(user?.has_seal || user?.pls_license_number || user?.gswcc_certs?.length);
  const isNew = !id || id === 'new';

  const [letter, setLetter] = useState({
    ...empty,
    related_type: searchParams.get('related_type') || null,
    related_id: searchParams.get('related_id') ? parseInt(searchParams.get('related_id'), 10) : null,
  });
  const [refLabel, setRefLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [refSearch, setRefSearch] = useState('');
  const [refResults, setRefResults] = useState([]);
  const [showRefPicker, setShowRefPicker] = useState(false);
  const debounceRef = useRef();

  // Client picker (auto-fills recipient block)
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const clientDebounceRef = useRef();
  const [emailHits, setEmailHits] = useState([]);        // recipient email autocomplete
  const emailDebounceRef = useRef();

  // Photos live on their own endpoint, so they are tracked apart from the letter
  // body and survive an unsaved edit to the text.
  const [photos, setPhotos] = useState([]);
  // Enclosures (PDFs, plats) travel with the letter but never render in it.
  const [attachments, setAttachments] = useState([]);
  // Template picker: offered on a brand-new letter, or on demand.
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // The TipTap instance, so "Insert at cursor" can reach it from the photo panel.
  const editorRef = useRef(null);

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const loadLetter = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/letters/${id}`, config);
      setLetter(res.data);
      setPhotos(res.data.photos || []);
      setAttachments(res.data.attachments || []);
      setRefLabel(res.data.related_label || '');
    } catch (err) {
      alert('Failed to load letter: ' + (err.response?.data?.error || err.message));
      navigate('/letters');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => {
    loadLetter();
  }, [loadLetter]);

  // Pre-fill reference label if jumping in with related_type/id query params (new letter)
  useEffect(() => {
    if (isNew && letter.related_type && letter.related_id && !refLabel) {
      (async () => {
        try {
          if (letter.related_type === 'quote') {
            const r = await axios.get(`${API_URL}/quotes/${letter.related_id}`, config);
            setRefLabel(`Quote ${r.data.quote_number}`);
          } else if (letter.related_type === 'job') {
            const r = await axios.get(`${API_URL}/jobs/${letter.related_id}`, config);
            setRefLabel(`Job ${r.data.job_number}`);
            // Auto-fill recipient from job's client info
            if (r.data.client_name && !letter.recipient_name) {
              setLetter(prev => ({ ...prev, recipient_name: r.data.client_name }));
            }
          } else if (letter.related_type === 'invoice') {
            const r = await axios.get(`${API_URL}/invoices/${letter.related_id}`, config);
            setRefLabel(`Invoice ${r.data.invoice_number}`);
          }
        } catch (err) {
          // Non-fatal — just leave it unlabeled
          console.warn('Could not preload reference label:', err);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (field, value) => {
    setLetter(prev => ({ ...prev, [field]: value }));
  };

  // ---- Fact block (the aligned label/value rows under the title) ----
  const fields = letter.fields || [];
  const setFields = (next) => setLetter(prev => ({ ...prev, fields: next }));
  const addField = () => setFields([...fields, { label: '', value: '' }]);
  const updateFieldRow = (i, key, val) =>
    setFields(fields.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));

  // Photos need a saved letter to hang off. Save first and hand back the id.
  const ensureSaved = async () => {
    if (!isNew && id) return id;
    const saved = await saveLetter(false);
    return saved?.id || null;
  };

  const insertPhotoAtCursor = (photoId) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.chain().focus().insertLetterPhoto(photoId).run();
  };

  const applyTemplate = (tpl) => {
    setLetter(prev => ({
      ...prev,
      subject: tpl.subject || prev.subject,
      body_html: tpl.body_html || '',
      fields: tpl.fields || [],
      closing: tpl.closing || prev.closing,
      apply_seal: canSeal ? !!tpl.apply_seal : false,
    }));
    setShowTemplates(false);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Save this letter as a template.\n\nTemplate name:',
      letter.subject || '');
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      await axios.post(`${API_URL}/letter-templates`, {
        name: name.trim(),
        subject: letter.subject || '',
        body_html: letter.body_html || '',
        fields: letter.fields || [],
        closing: letter.closing || 'Sincerely,',
        apply_seal: !!letter.apply_seal,
      }, config);
      alert(`Saved "${name.trim()}" as a template.`);
    } catch (err) {
      alert('Could not save template: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingTemplate(false);
    }
  };

  // Warn before a letter goes out with [Insert ...] still in it.
  const confirmPlaceholders = (action) => {
    const found = findLetterPlaceholders(letter.body_html, letter.fields);
    if (!found.length) return true;
    return window.confirm(
      `${found.length} placeholder${found.length === 1 ? '' : 's'} still need filling in:\n\n`
      + found.slice(0, 12).join('\n')
      + (found.length > 12 ? `\n...and ${found.length - 12} more` : '')
      + `\n\n${action} anyway?`
    );
  };

  const handleRefSearch = (val) => {
    setRefSearch(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 1) {
      setRefResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/letters/reference-search?q=${encodeURIComponent(val)}`, config);
        setRefResults(res.data || []);
      } catch (err) {
        console.error('Reference search failed:', err);
      }
    }, 200);
  };

  const handleClientSearch = (val) => {
    setClientSearch(val);
    clearTimeout(clientDebounceRef.current);
    if (val.trim().length < 1) {
      setClientResults([]);
      return;
    }
    clientDebounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/clients?search=${encodeURIComponent(val)}&limit=10`, config);
        setClientResults(res.data?.clients || []);
      } catch (err) {
        console.error('Client search failed:', err);
      }
    }, 200);
  };

  // Recipient email autocomplete — same contact source as the email composer. Only the
  // address being typed (after the last comma) is looked up, so you can add several.
  const handleRecipientEmailChange = (val) => {
    updateField('recipient_email', val);
    clearTimeout(emailDebounceRef.current);
    const tok = lastToken(val);
    if (tok.length < 2) { setEmailHits([]); return; }
    emailDebounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/email-contacts?q=${encodeURIComponent(tok)}`, config);
        setEmailHits(res.data || []);
      } catch (err) {
        setEmailHits([]);
      }
    }, 220);
  };

  const pickRecipientEmail = (email) => {
    const prefix = (letter.recipient_email || '').split(',').slice(0, -1).map(s => s.trim()).filter(Boolean);
    updateField('recipient_email', [...prefix, email].join(', ') + ', ');
    setEmailHits([]);
  };

  const pickClient = (client) => {
    const csz = [client.city, client.state].filter(Boolean).join(', ');
    const cityStateZip = [csz, client.zip_code].filter(Boolean).join(' ').trim();
    setLetter(prev => ({
      ...prev,
      recipient_name: client.name || prev.recipient_name,
      recipient_company: client.company_name || prev.recipient_company,
      recipient_address: client.address || prev.recipient_address,
      recipient_city_state_zip: cityStateZip || prev.recipient_city_state_zip,
      recipient_email: client.email || prev.recipient_email,
    }));
    setShowClientPicker(false);
    setClientSearch('');
    setClientResults([]);
  };

  const pickReference = (item) => {
    setLetter(prev => ({ ...prev, related_type: item.type, related_id: item.id }));
    setRefLabel(item.label);
    setShowRefPicker(false);
    setRefSearch('');
    setRefResults([]);
  };

  const clearReference = () => {
    setLetter(prev => ({ ...prev, related_type: null, related_id: null, attach_reference_pdf: false }));
    setRefLabel('');
  };

  const buildPayload = () => ({
    recipient_name: letter.recipient_name || null,
    recipient_company: letter.recipient_company || null,
    recipient_address: letter.recipient_address || null,
    recipient_city_state_zip: letter.recipient_city_state_zip || null,
    recipient_email: splitRecipients(letter.recipient_email).join(', ') || null,
    subject: letter.subject || null,
    body_html: letter.body_html || '',
    related_type: letter.related_type || null,
    related_id: letter.related_id || null,
    attach_reference_pdf: !!letter.attach_reference_pdf,
    fields: (letter.fields || []).filter(f => (f.label || '').trim() || (f.value || '').trim()),
    closing: letter.closing || 'Sincerely,',
    apply_seal: !!letter.apply_seal,
    visibility: letter.visibility || 'public',
  });

  const saveLetter = async (showAlert = true) => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let res;
      if (isNew) {
        res = await axios.post(`${API_URL}/letters`, payload, config);
        navigate(`/letters/${res.data.id}`, { replace: true });
      } else {
        res = await axios.patch(`${API_URL}/letters/${id}`, payload, config);
      }
      setLetter(res.data);
      setPhotos(res.data.photos || []);
      setAttachments(res.data.attachments || []);
      setRefLabel(res.data.related_label || '');
      if (showAlert) alert('Letter saved.');
      return res.data;
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const previewLetter = async () => {
    if (!confirmPlaceholders('Preview')) return;
    if (isNew) {
      const saved = await saveLetter(false);
      if (!saved) return;
    } else {
      await saveLetter(false);
    }
    const currentId = isNew ? null : id;
    if (currentId) {
      // Pass token as query param so the new tab (which can't read localStorage / axios headers) authenticates
      window.open(`${API_URL}/letters/${currentId}/pdf?token=${encodeURIComponent(token)}`, '_blank');
    }
  };

  const sendLetter = async () => {
    const recips = splitRecipients(letter.recipient_email);
    if (!recips.length) {
      alert('Add a recipient email address before sending.');
      return;
    }
    const bad = recips.filter(e => !e.includes('@'));
    if (bad.length) {
      alert(`This doesn't look like a valid email address:\n\n${bad.join('\n')}`);
      return;
    }
    if (!letter.subject) {
      if (!window.confirm('This letter has no subject. Send anyway?')) return;
    }
    if (!confirmPlaceholders('Send')) return;
    const saved = isNew ? await saveLetter(false) : await saveLetter(false);
    if (!saved) return;
    const currentId = saved.id || id;
    const who = recips.length === 1 ? recips[0] : `these ${recips.length} recipients:\n\n${recips.join('\n')}`;
    if (!window.confirm(`Send this letter to ${who}?`)) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/letters/${currentId}/send`, { to_email: recips.join(', ') }, config);
      alert('Letter sent successfully.');
      const res = await axios.get(`${API_URL}/letters/${currentId}`, config);
      setLetter(res.data);
    } catch (err) {
      alert('Failed to send: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;
  }

  const labelStyle = { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: '4px' };
  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/letters')}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
            backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db',
            borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
          }}
        >
          <FiArrowLeft size={14} /> Back to Letters
        </button>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowTemplates(true)}
            style={{
              padding: '10px 16px', backgroundColor: 'white', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <FiFileText size={14} /> Templates
          </button>
          <button
            onClick={saveAsTemplate}
            disabled={savingTemplate}
            title="Save this letter's title, details block and body as a reusable template"
            style={{
              padding: '10px 16px', backgroundColor: 'white', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: '8px',
              cursor: savingTemplate ? 'wait' : 'pointer',
              fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <FiBookmark size={14} /> {savingTemplate ? 'Saving...' : 'Save as Template'}
          </button>
          <button
            onClick={previewLetter}
            disabled={saving}
            style={{
              padding: '10px 16px', backgroundColor: 'white', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: '8px', cursor: saving ? 'wait' : 'pointer',
              fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <FiPrinter size={14} /> Preview / Print
          </button>
          <button
            onClick={() => saveLetter(true)}
            disabled={saving}
            style={{
              padding: '10px 16px', backgroundColor: '#f3f4f6', color: '#111827',
              border: '1px solid #d1d5db', borderRadius: '8px', cursor: saving ? 'wait' : 'pointer',
              fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <FiSave size={14} /> {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={sendLetter}
            disabled={sending}
            style={{
              padding: '10px 18px', backgroundColor: '#16a34a', color: 'white',
              border: 'none', borderRadius: '8px', cursor: sending ? 'wait' : 'pointer',
              fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <FiSend size={14} /> {sending ? 'Sending...' : 'Save & Send'}
          </button>
        </div>
      </div>

      <h1 style={{ margin: '0 0 4px', fontSize: '22px', color: '#111827' }}>
        {isNew ? 'New Letter' : (letter.subject || 'Untitled Letter')}
      </h1>
      <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '13px' }}>
        Status: <strong style={{ textTransform: 'capitalize' }}>{letter.status}</strong>
        {letter.sent_at && <> · Sent {new Date(letter.sent_at + 'Z').toLocaleString()}</>}
      </p>

      {/* Recipient + meta */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Client picker — auto-fills the recipient fields below */}
        <div style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f3f4f6' }}>
          <label style={labelStyle}>Fill from existing client</label>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <FiUser style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Search client by name, company, or email..."
                value={clientSearch}
                onChange={(e) => { setShowClientPicker(true); handleClientSearch(e.target.value); }}
                onFocus={() => setShowClientPicker(true)}
                style={{ ...inputStyle, paddingLeft: '32px' }}
              />
            </div>
            {showClientPicker && clientResults.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '320px', overflowY: 'auto',
              }}>
                {clientResults.map(c => (
                  <div
                    key={c.id}
                    onClick={() => pickClient(c)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{c.name}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {[c.company_name, c.email, c.city && c.state ? `${c.city}, ${c.state}` : null].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showClientPicker && clientSearch.trim() && clientResults.length === 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, padding: '12px 14px',
                color: '#9ca3af', fontSize: '13px',
              }}>
                No clients match "{clientSearch}".
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Recipient name</label>
            <input style={inputStyle} value={letter.recipient_name || ''} onChange={(e) => updateField('recipient_name', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Company</label>
            <input style={inputStyle} value={letter.recipient_company || ''} onChange={(e) => updateField('recipient_company', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Address</label>
            <input style={inputStyle} value={letter.recipient_address || ''} onChange={(e) => updateField('recipient_address', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>City, State ZIP</label>
            <input style={inputStyle} value={letter.recipient_city_state_zip || ''} onChange={(e) => updateField('recipient_city_state_zip', e.target.value)} />
          </div>
          <div style={{ position: 'relative' }}>
            <label style={labelStyle}>Email</label>
            <input
              type="text"
              style={inputStyle}
              value={letter.recipient_email || ''}
              onChange={(e) => handleRecipientEmailChange(e.target.value)}
              placeholder="name@company.com — separate multiple with commas"
              autoComplete="off"
            />
            {emailHits.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60, background: 'white',
                border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 2,
                boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: 210, overflowY: 'auto',
              }}>
                {emailHits.map((h, i) => (
                  <button key={h.email + i} type="button" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickRecipientEmail(h.email)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', borderTop: i ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', fontSize: '13px' }}>
                    <span style={{ fontWeight: 600 }}>{h.name}</span>
                    <span style={{ color: '#64748b' }}> · {h.email}</span>
                    {h.detail ? <span style={{ display: 'block', color: '#94a3b8', fontSize: '11.5px' }}>{h.detail}</span> : null}
                  </button>
                ))}
              </div>
            )}
            {splitRecipients(letter.recipient_email).length > 1 && (
              <div style={{ fontSize: '11.5px', color: '#6b7280', marginTop: '4px' }}>
                {splitRecipients(letter.recipient_email).length} recipients
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={labelStyle}>Subject</label>
          <input style={inputStyle} value={letter.subject || ''} onChange={(e) => updateField('subject', e.target.value)} placeholder="e.g. Re: Boundary Survey Completion" />
        </div>

        {/* Aligned fact block — prints as a label/value table under the title */}
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Details block</label>
            <button
              type="button"
              onClick={addField}
              style={{
                padding: '4px 10px', background: 'white', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <FiPlus size={12} /> Add row
            </button>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
            Prints as an aligned list under the title — e.g. Property Owner, Site Address, Parcel ID, County.
          </div>
          {fields.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
              No detail rows. The letter will go straight from the title into the body.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {fields.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                  <input
                    style={{ ...inputStyle, flex: '0 0 34%', fontWeight: 600 }}
                    value={f.label || ''}
                    onChange={(e) => updateFieldRow(i, 'label', e.target.value)}
                    placeholder="Label"
                  />
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={f.value || ''}
                    onChange={(e) => updateFieldRow(i, 'value', e.target.value)}
                    placeholder="Value"
                  />
                  <button
                    type="button"
                    onClick={() => removeField(i)}
                    title="Remove row"
                    style={{
                      padding: '8px 9px', background: 'white', color: '#dc2626',
                      border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', lineHeight: 0,
                    }}
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Closing + seal */}
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 200px' }}>
              <label style={labelStyle}>Closing</label>
              <select
                style={inputStyle}
                value={letter.closing || 'Sincerely,'}
                onChange={(e) => updateField('closing', e.target.value)}
              >
                {CLOSINGS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '260px' }}>
              <label style={labelStyle}>Seal</label>
              <label style={{
                display: 'inline-flex', alignItems: 'flex-start', gap: '8px',
                cursor: canSeal ? 'pointer' : 'not-allowed', fontSize: '13px',
                color: canSeal ? '#374151' : '#9ca3af',
              }}>
                <input
                  type="checkbox"
                  disabled={!canSeal}
                  checked={!!letter.apply_seal}
                  onChange={(e) => updateField('apply_seal', e.target.checked)}
                  style={{ marginTop: '2px' }}
                />
                <span>
                  <FiAward size={13} style={{ verticalAlign: '-2px', marginRight: '4px', color: letter.apply_seal ? '#0f2942' : '#9ca3af' }} />
                  Apply my seal and license numbers
                  <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {canSeal
                      ? 'Stamps your seal, signature and registration numbers on the signature block.'
                      : 'Add your seal and license numbers on your profile first.'}
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Reference picker */}
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
          <label style={labelStyle}>Reference (optional)</label>
          {letter.related_type && refLabel ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ padding: '6px 12px', backgroundColor: '#eff6ff', color: '#2563eb', borderRadius: '6px', fontSize: '13px', fontWeight: 600 }}>
                {refLabel}
              </span>
              <button
                onClick={clearReference}
                style={{ padding: '4px 8px', backgroundColor: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
              >
                <FiX size={12} /> Clear
              </button>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', marginLeft: '12px' }}>
                <input
                  type="checkbox"
                  checked={!!letter.attach_reference_pdf}
                  onChange={(e) => updateField('attach_reference_pdf', e.target.checked)}
                />
                <FiPaperclip size={12} /> Attach a copy of the {letter.related_type} when emailing
              </label>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <FiSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search quote / job / invoice number or name..."
                  value={refSearch}
                  onChange={(e) => { setShowRefPicker(true); handleRefSearch(e.target.value); }}
                  onFocus={() => setShowRefPicker(true)}
                  style={{ ...inputStyle, paddingLeft: '32px' }}
                />
              </div>
              {showRefPicker && refResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                  backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '320px', overflowY: 'auto',
                }}>
                  {refResults.map(item => (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={() => pickReference(item)}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{item.label}</div>
                      {item.detail && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{item.detail}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Visibility (admin-only setting) */}
        {isAdmin && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
              <input
                type="checkbox"
                checked={letter.visibility === 'admin_only'}
                onChange={(e) => updateField('visibility', e.target.checked ? 'admin_only' : 'public')}
              />
              <FiLock size={13} style={{ color: letter.visibility === 'admin_only' ? '#dc2626' : '#9ca3af' }} />
              Admin only — hide this letter from non-admin staff
            </label>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ marginBottom: '20px' }}>
        <label style={labelStyle}>Body</label>
        <LetterEditor
          value={letter.body_html}
          onChange={(html) => updateField('body_html', html)}
          photos={photos}
          onReady={(editor) => { editorRef.current = editor; }}
        />
      </div>

      {showTemplates && (
        <LetterTemplatePicker
          onPick={applyTemplate}
          onClose={() => setShowTemplates(false)}
          hasContent={!!(letter.body_html || '').replace(/<[^>]*>/g, '').trim()}
        />
      )}

      {/* Photos */}
      <LetterPhotoPanel
        letterId={isNew ? null : Number(id)}
        photos={photos}
        onPhotosChange={setPhotos}
        onInsertInline={insertPhotoAtCursor}
        onRequireSave={ensureSaved}
      />

      {/* Enclosures */}
      <LetterAttachmentPanel
        letterId={isNew ? null : Number(id)}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        onRequireSave={ensureSaved}
      />
    </div>
  );
}

export default LetterEdit;
